/**
 * NotchesReportsPage -- weekly reports archive.
 *
 * Read-only view of `notches-weekly-reports-prod` rows the caller is
 * entitled to. BB members see the global BB report; Notches subscribers
 * see their per-recipient report; users with both entitlements see two
 * tabs and can switch.
 *
 * Renders the HTML body verbatim -- matches what the Sunday email sends.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  weeklyReportsService,
  WeeklyReportSummary,
  WeeklyReportFull,
  ReportAudience,
} from '../../services/weeklyReportsService';
import '../Dashboard.css';

// Sanitize the email HTML before rendering. The source is our own server
// Lambda so the threat is low, BUT the Lambda includes AI-generated
// commentary (Claude/ChatGPT) which is a soft prompt-injection vector --
// a hostile AI response could in theory smuggle in active markup. Defense
// in depth: strip scripts/iframes/event handlers + restrict to the tags
// the email actually uses (tables, inline styles, images, basic formatting).
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'caption', 'code', 'div', 'em', 'figure',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
    'pre', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody',
    'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
  ],
  ALLOWED_ATTR: [
    'align', 'alt', 'border', 'cellpadding', 'cellspacing', 'class', 'colspan',
    'height', 'href', 'rowspan', 'src', 'style', 'target', 'title', 'valign',
    'width',
  ],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  ALLOW_DATA_ATTR: false,
};

const AUDIENCE_LABEL: Record<ReportAudience, string> = {
  bb: 'Boring Bucket',
  notches: 'Notches',
};

function fmtGenerated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}

export const NotchesReportsPage: React.FC = () => {
  const { token } = useAuth();
  const [audiences, setAudiences] = useState<ReportAudience[]>([]);
  const [bbList, setBbList] = useState<WeeklyReportSummary[]>([]);
  const [notchesList, setNotchesList] = useState<WeeklyReportSummary[]>([]);
  const [activeTab, setActiveTab] = useState<ReportAudience | null>(null);
  const [activeWeek, setActiveWeek] = useState<string | null>(null);
  const [currentReport, setCurrentReport] = useState<WeeklyReportFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  // Initial list load
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    weeklyReportsService.list(token)
      .then((r) => {
        if (cancelled) return;
        setAudiences(r.audiences);
        setBbList(r.bb || []);
        setNotchesList(r.notches || []);
        // Default tab: BB if available, else Notches.
        const defaultTab: ReportAudience | null =
          r.audiences.includes('bb') ? 'bb'
          : r.audiences.includes('notches') ? 'notches'
          : null;
        setActiveTab(defaultTab);
        // Default week: newest available on the default tab.
        const defaultList = defaultTab === 'bb' ? r.bb : r.notches;
        if (defaultList && defaultList.length > 0) {
          setActiveWeek(defaultList[0].weekIso);
        }
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load reports'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // When tab or week changes, fetch the report body.
  useEffect(() => {
    if (!token || !activeTab || !activeWeek) { setCurrentReport(null); return; }
    let cancelled = false;
    setBodyLoading(true);
    setCurrentReport(null);
    weeklyReportsService.get(token, activeTab, activeWeek)
      .then((r) => { if (!cancelled) setCurrentReport(r); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load report'); })
      .finally(() => { if (!cancelled) setBodyLoading(false); });
    return () => { cancelled = true; };
  }, [token, activeTab, activeWeek]);

  const activeList = useMemo(() => {
    if (activeTab === 'bb') return bbList;
    if (activeTab === 'notches') return notchesList;
    return [];
  }, [activeTab, bbList, notchesList]);

  const shell = (body: React.ReactNode) => (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px', color: '#e5e7eb' }}>
        {body}
      </div>
    </div>
  );

  if (loading) return shell(<div style={{ color: '#94a3b8' }}>Loading weekly reports...</div>);
  if (error)   return shell(<div style={{ color: '#ef4444' }}>{error}</div>);
  if (audiences.length === 0) {
    return shell(<div style={{ color: '#94a3b8' }}>No weekly reports available for your subscription.</div>);
  }

  return shell(
    <>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 4, marginTop: 0 }}>Weekly Reports</h1>
      <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
        Sunday 4pm ET recap and week-ahead outlook. Same content delivered to your inbox.
      </p>

      {/* Audience tabs -- only render when user has both */}
      {audiences.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #1e293b' }}>
          {audiences.map((a) => {
            const active = a === activeTab;
            const list = a === 'bb' ? bbList : notchesList;
            return (
              <button
                key={a}
                onClick={() => {
                  setActiveTab(a);
                  if (list.length > 0) setActiveWeek(list[0].weekIso);
                  else setActiveWeek(null);
                }}
                style={{
                  background: 'transparent',
                  color: active ? '#a78bfa' : '#94a3b8',
                  border: 'none',
                  borderBottom: active ? '2px solid #a78bfa' : '2px solid transparent',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: active ? 700 : 500,
                  marginBottom: -1,
                }}
              >
                {AUDIENCE_LABEL[a]}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 18 }}>
        {/* Week list (left) */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5, marginBottom: 8 }}>
            ARCHIVE
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            {activeList.length === 0 && (
              <div style={{ color: '#64748b', fontSize: 13 }}>No reports yet.</div>
            )}
            {activeList.map((w) => {
              const active = w.weekIso === activeWeek;
              return (
                <button
                  key={w.weekIso}
                  onClick={() => setActiveWeek(w.weekIso)}
                  style={{
                    textAlign: 'left',
                    background: active ? '#1e293b' : '#111827',
                    color: active ? '#f1f5f9' : '#cbd5e1',
                    border: `1px solid ${active ? '#a78bfa' : '#1f2937'}`,
                    borderRadius: 6,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{w.weekLabel}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {w.weekIso} &middot; sent {fmtGenerated(w.generatedAt)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Report body (right) */}
        <div>
          {bodyLoading && <div style={{ color: '#94a3b8' }}>Loading report...</div>}
          {!bodyLoading && currentReport && (
            <div style={{ background: '#ffffff', borderRadius: 8, overflow: 'hidden' }}>
              {/* Sanitized with DOMPurify before injection (see SANITIZE_CONFIG
                  above). The email HTML is self-contained: white background,
                  dark text, tables + inline styles. */}
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(currentReport.htmlBody, SANITIZE_CONFIG) as string,
                }}
                style={{ color: '#111827' }}
              />
            </div>
          )}
          {!bodyLoading && !currentReport && activeWeek && (
            <div style={{ color: '#94a3b8' }}>Select a week from the archive.</div>
          )}
        </div>
      </div>
    </>
  );
};
